import { useEffect, useRef } from 'react';
import { DOC } from './document';

const ACCENT = '#FF7A45';
const INK = '#FFFFFF';
const MUTED = 'rgba(232,232,234,0.45)';
const RULE = '#26272A';
const PAPER = '#0F0F11';

const CYCLE = 11;
const SKEW_DEG = -1.2;

// One misread per cycle so the system has something to correct (line index, wrong → right).
const CORRECTION = { line: 9, wrong: 'Rampiril', right: 'Ramipril' };

type Props = { cycleSeconds?: number };

export default function OcrMotionA({ cycleSeconds = CYCLE }: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let widthCss = 0;
    let heightCss = 0;
    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      widthCss = rect.width;
      heightCss = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(widthCss * dpr);
      canvas.height = Math.floor(heightCss * dpr);
      canvas.style.width = `${widthCss}px`;
      canvas.style.height = `${heightCss}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    const start = performance.now();
    let rafId = 0;

    const fontFor = (em: string | undefined) => {
      if (em === 'title') return '600 13px "Inter", system-ui, sans-serif';
      if (em === 'subtitle') return '500 11px "Inter", system-ui, sans-serif';
      if (em === 'small') return 'italic 10px "Inter", system-ui, sans-serif';
      return '11px "Inter", system-ui, sans-serif';
    };
    const lineHFor = (em: string | undefined) =>
      em === 'title' ? 22 : em === 'subtitle' ? 18 : 17;

    const draw = (now: number) => {
      const t = (now - start) / 1000;
      const W = widthCss;
      const H = heightCss;
      ctx.clearRect(0, 0, W, H);

      // Panels.
      const padX = 20;
      const padY = 24;
      const gap = 24;
      const docW = (W - padX * 2 - gap) * 0.5;
      const trW = W - padX * 2 - gap - docW;
      const docX = padX;
      const docY = padY;
      const docH = H - padY * 2 - 22;
      const trX = padX + docW + gap;
      const trY = padY;
      const trH = docH;

      // Transcript panel — drawn first so source can sit on top with skew.
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.fillRect(trX, trY, trW, trH);
      ctx.strokeStyle = RULE;
      ctx.lineWidth = 1;
      ctx.strokeRect(trX + 0.5, trY + 0.5, trW - 1, trH - 1);

      const cyc = reduced ? 1 : (t / cycleSeconds) % 1;

      // Skewed source page.
      ctx.save();
      const cx = docX + docW / 2;
      const cy = docY + docH / 2;
      ctx.translate(cx, cy);
      ctx.rotate((SKEW_DEG * Math.PI) / 180);
      ctx.translate(-cx, -cy);

      // Drop shadow.
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(docX + 4, docY + 4, docW, docH);
      // Page.
      ctx.fillStyle = PAPER;
      ctx.fillRect(docX, docY, docW, docH);
      ctx.strokeStyle = RULE;
      ctx.lineWidth = 1;
      ctx.strokeRect(docX + 0.5, docY + 0.5, docW - 1, docH - 1);

      // Inner content rect.
      const innerX = docX + 18;
      const innerY = docY + 22;
      const innerW = docW - 36;

      // Compute line y positions.
      const lineYs: number[] = [];
      let yCursor = innerY + 4;
      for (const l of DOC) {
        lineYs.push(yCursor);
        yCursor += lineHFor(l.emphasis);
      }
      const lastY = yCursor;
      // Scan beam y is in source-panel space (post-rotation).
      const beamY = innerY + cyc * (lastY - innerY);

      // Text — fade by beam proximity (faint ahead of beam, full behind).
      DOC.forEach((line, i) => {
        const ly = lineYs[i]!;
        const lh = lineHFor(line.emphasis);
        const passed = ly + lh * 0.5 < beamY;
        const ahead = ly - lh * 0.5 > beamY;
        let alpha;
        if (passed) alpha = 0.9;
        else if (ahead) alpha = 0.22;
        else alpha = 0.55; // currently under the beam

        let text = line.text;
        // Correction beat — show wrong reading for first ~250ms after beam crosses the word.
        if (i === CORRECTION.line && passed) {
          const dt = (beamY - (ly + lh * 0.5)) / lh;
          if (dt < 0.6) text = line.text.replace(CORRECTION.right, CORRECTION.wrong);
        }

        ctx.fillStyle =
          line.emphasis === 'title' ? `rgba(255,255,255,${alpha})` : `rgba(232,232,234,${alpha})`;
        ctx.font = fontFor(line.emphasis);
        ctx.textBaseline = 'top';
        ctx.fillText(text, innerX, ly);

        // Bounding box on already-read lines.
        if (passed) {
          ctx.strokeStyle = 'rgba(255,122,69,0.22)';
          ctx.lineWidth = 1;
          const tw = Math.min(innerW, ctx.measureText(text).width + 6);
          ctx.strokeRect(innerX - 3, ly - 2, tw + 6, lh - 2);
        }
      });

      // Scan beam.
      if (!reduced) {
        const grad = ctx.createLinearGradient(0, beamY - 26, 0, beamY + 26);
        grad.addColorStop(0, 'rgba(255,122,69,0)');
        grad.addColorStop(0.5, 'rgba(255,122,69,0.28)');
        grad.addColorStop(1, 'rgba(255,122,69,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(docX, beamY - 26, docW, 52);
        ctx.strokeStyle = ACCENT;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(docX, beamY);
        ctx.lineTo(docX + docW, beamY);
        ctx.stroke();
      }

      ctx.restore();

      // Transcript content (un-skewed).
      const trPadX = 16;
      const trPadY = 22;
      const trInnerX = trX + trPadX;
      const trInnerY = trY + trPadY;
      const linesPassed = DOC.filter(
        (line, i) => lineYs[i]! + lineHFor(line.emphasis) * 0.5 < beamY,
      ).length;

      ctx.font = '11px "JetBrains Mono", ui-monospace, monospace';
      ctx.textBaseline = 'top';
      let ty = trInnerY;
      for (let i = 0; i < Math.min(DOC.length, linesPassed + 1); i++) {
        const line = DOC[i]!;
        const isCurrent = i === linesPassed && !reduced;
        const conf = 0.78 + ((i * 13) % 22) / 100;
        const lowConf = i === CORRECTION.line; // the corrected line stays "low conf"
        // Line number.
        ctx.fillStyle = 'rgba(232,232,234,0.3)';
        ctx.fillText(String(i + 1).padStart(2, '0'), trInnerX, ty);
        // Body.
        ctx.fillStyle = isCurrent ? ACCENT : INK;
        ctx.fillText(line.text, trInnerX + 26, ty);
        // Confidence pill.
        const confText = lowConf ? '0.71' : conf.toFixed(2);
        const pillX = trX + trW - 44;
        ctx.fillStyle = lowConf ? 'rgba(255,122,69,0.18)' : 'rgba(232,232,234,0.08)';
        ctx.fillRect(pillX - 4, ty - 1, 36, 14);
        ctx.fillStyle = lowConf ? ACCENT : 'rgba(232,232,234,0.55)';
        ctx.font = '10px "JetBrains Mono", ui-monospace, monospace';
        ctx.fillText(confText, pillX, ty + 1);
        ctx.font = '11px "JetBrains Mono", ui-monospace, monospace';
        ty += 16;
        if (ty > trY + trH - 24) break;
      }

      // Labels.
      ctx.fillStyle = MUTED;
      ctx.font = '10px "JetBrains Mono", ui-monospace, monospace';
      ctx.fillText('SCAN · ARZTBRIEF.PDF', docX, docY - 10);
      ctx.fillText('OCR · STREAM', trX, trY - 10);
      ctx.fillStyle = ACCENT;
      const lineLabel = `LINE ${Math.min(DOC.length, linesPassed + 1)
        .toString()
        .padStart(2, '0')}/${DOC.length}`;
      const lw = ctx.measureText(lineLabel).width;
      ctx.fillText(lineLabel, trX + trW - lw, trY - 10);

      // Footer status bar.
      const fY = docY + docH + 6;
      ctx.fillStyle = MUTED;
      ctx.font = '10px "JetBrains Mono", ui-monospace, monospace';
      const tokSec = Math.floor(58 + Math.sin(t * 1.8) * 4);
      ctx.fillText(
        `model: ocr-de-v1.4   gpu: de-fra1-a100   batch: 12   ${tokSec} tok/s   audit: signed`,
        docX,
        fY,
      );

      if (!reduced) rafId = requestAnimationFrame(draw);
    };

    if (reduced) draw(start + cycleSeconds * 1000);
    else rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [cycleSeconds]);

  return (
    <div ref={wrapRef} className="relative aspect-[16/7] w-full bg-black">
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}
